import { useState } from 'react';
import { useChild } from '../../contexts/ChildContext';
import { useAuth } from '../../contexts/AuthContext';
import { createInvite } from '../../hooks/useInvites';

const ChildSelector = ({ onChildSelected } = {}) => {
  const { selectedChild, setSelectedChild, childList, addChild, updateChild, deleteChild } = useChild();
  const { user } = useAuth();

  const [showModal, setShowModal]     = useState(false);
  const [formData, setFormData]       = useState({ name: '', dateOfBirth: '', gender: '' });
  const [isLoading, setIsLoading]     = useState(false);
  const [error, setError]             = useState(null);

  // Caregiver modal state
  const [showCaregiverModal, setShowCaregiverModal] = useState(false);
  const [caregiverChild, setCaregiverChild]         = useState(null);
  const [caregiverEmail, setCaregiverEmail]         = useState('');
  const [caregiverError, setCaregiverError]         = useState(null);
  // Map of email → invite URL for this modal session
  const [inviteLinks, setInviteLinks] = useState({});
  const [inviteLoading, setInviteLoading] = useState(null); // email being generated
  const [copiedEmail, setCopiedEmail] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddChild = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.dateOfBirth) return;
    setIsLoading(true);
    setError(null);
    try {
      await addChild(formData);
      setFormData({ name: '', dateOfBirth: '', gender: '' });
      setShowModal(false);
    } catch (err) {
      console.error('Error adding child:', err);
      setError('Failed to add child. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteChild = async (childId) => {
    if (window.confirm('Are you sure you want to delete this child profile?')) {
      setIsLoading(true);
      setError(null);
      try {
        await deleteChild(childId);
      } catch (err) {
        console.error('Error deleting child:', err);
        setError('Failed to delete child. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleOpenCaregivers = (child) => {
    setCaregiverChild({ ...child });
    setCaregiverEmail('');
    setCaregiverError(null);
    setInviteLinks({});
    setCopiedEmail(null);
    setShowCaregiverModal(true);
  };

  const handleAddCaregiver = async () => {
    const email = caregiverEmail.trim().toLowerCase();
    if (!email) return;
    const existing = caregiverChild.caregivers || [];
    if (existing.includes(email)) {
      setCaregiverError('This email is already a caregiver.');
      return;
    }
    const updated = [...existing, email];
    try {
      await updateChild(caregiverChild.id, { caregivers: updated });
      setCaregiverChild((prev) => ({ ...prev, caregivers: updated }));
      setCaregiverEmail('');
      setCaregiverError(null);
      // Auto-generate an invite link for the newly added caregiver
      handleGenerateInvite(email, caregiverChild.id, caregiverChild.name);
    } catch (err) {
      console.error('Error adding caregiver:', err);
      setCaregiverError('Failed to add caregiver. Please try again.');
    }
  };

  const handleRemoveCaregiver = async (email) => {
    const updated = (caregiverChild.caregivers || []).filter((e) => e !== email);
    try {
      await updateChild(caregiverChild.id, { caregivers: updated });
      setCaregiverChild((prev) => ({ ...prev, caregivers: updated }));
      setInviteLinks((prev) => { const next = { ...prev }; delete next[email]; return next; });
    } catch (err) {
      console.error('Error removing caregiver:', err);
      setCaregiverError('Failed to remove caregiver. Please try again.');
    }
  };

  const handleGenerateInvite = async (email, childId, childName) => {
    setInviteLoading(email);
    try {
      const url = await createInvite({
        childId: childId || caregiverChild.id,
        childName: childName || caregiverChild.name,
        ownerEmail: user.email,
        caregiverEmail: email,
      });
      setInviteLinks((prev) => ({ ...prev, [email]: url }));
    } catch (err) {
      console.error('Error generating invite:', err);
      setCaregiverError('Failed to generate invite link.');
    } finally {
      setInviteLoading(null);
    }
  };

  const handleCopyInvite = (email) => {
    const url = inviteLinks[email];
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2500);
    });
  };

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 className="mb-0">👶 Select Child Profile</h5>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowModal(true)}
              disabled={isLoading}
            >
              ➕ Add Child
            </button>
          </div>
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger mb-3">{error}</div>}
          {childList.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">👶</div>
              <p>No children added yet. Create your first child profile to get started!</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
              {childList.map((child) => (
                <div key={child.id} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <button
                    type="button"
                    className={`btn ${selectedChild?.id === child.id ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => { setSelectedChild(child); onChildSelected?.(); }}
                    style={{ borderRadius: '50px', paddingLeft: '1rem', paddingRight: '1rem' }}
                  >
                    👶 {child.name}
                    {child.viewerOnly && (
                      <span style={{ fontSize: '0.7rem', marginLeft: '0.4rem', opacity: 0.75 }}>
                        (view only)
                      </span>
                    )}
                  </button>
                  {!child.viewerOnly && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => handleOpenCaregivers(child)}
                      title="Manage caregivers"
                      style={{ borderRadius: '50px', padding: '0.25rem 0.6rem', fontSize: '0.8rem' }}
                    >
                      👥
                    </button>
                  )}
                  {childList.filter(c => !c.viewerOnly).length > 1 && !child.viewerOnly && (
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-danger"
                      onClick={() => handleDeleteChild(child.id)}
                      disabled={isLoading}
                      title="Delete child"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Add Child Modal ─────────────────────────────────────────── */}
      {showModal && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          role="dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">👶 Add New Child</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <form onSubmit={handleAddChild}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Child's Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="name"
                      name="name"
                      placeholder="e.g., Emma"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      autoFocus
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="dateOfBirth" className="form-label">
                      Date of Birth <span className="required">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="gender" className="form-label">
                      Gender{' '}
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>(Optional)</span>
                    </label>
                    <select
                      className="form-select"
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                    >
                      <option value="">Select gender</option>
                      <option value="male">👦 Male</option>
                      <option value="female">👧 Female</option>
                      <option value="other">➕ Other</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isLoading}>
                    {isLoading
                      ? <><span className="spinner-border spinner-border-sm me-2" />Adding…</>
                      : '✓ Add Child'
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Manage Caregivers Modal ──────────────────────────────────── */}
      {showCaregiverModal && caregiverChild && (
        <div
          className="modal d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          role="dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setShowCaregiverModal(false); }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">👥 Caregivers — {caregiverChild.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowCaregiverModal(false)} />
              </div>
              <div className="modal-body">

                <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
                  Caregivers have full read and write access to this child's dashboard.
                  Add their email, then share the invite link so they know where to sign up.
                </p>

                {caregiverError && (
                  <div className="alert alert-danger mb-3">{caregiverError}</div>
                )}

                {/* Existing caregivers */}
                {(caregiverChild.caregivers || []).length === 0 ? (
                  <p style={{ color: 'var(--text-light)', fontSize: '0.875rem', textAlign: 'center', padding: '0.75rem 0' }}>
                    No caregivers added yet.
                  </p>
                ) : (
                  <div style={{ marginBottom: '1.25rem' }}>
                    {(caregiverChild.caregivers || []).map((email) => (
                      <div key={email} style={{
                        background: 'var(--bg-light)',
                        border: '1px solid var(--border-light)',
                        borderRadius: '8px',
                        padding: '0.75rem 1rem',
                        marginBottom: '0.5rem',
                      }}>
                        {/* Email row */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: inviteLinks[email] ? '0.5rem' : 0 }}>
                          <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-dark)' }}>
                            📧 {email}
                          </span>
                          <div style={{ display: 'flex', gap: '0.375rem' }}>
                            {!inviteLinks[email] && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => handleGenerateInvite(email)}
                                disabled={inviteLoading === email}
                                title="Generate invite link"
                                style={{ fontSize: '0.75rem' }}
                              >
                                {inviteLoading === email
                                  ? <span className="spinner-border spinner-border-sm" />
                                  : '🔗 Invite link'
                                }
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleRemoveCaregiver(email)}
                              style={{ fontSize: '0.75rem' }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        {/* Invite link row */}
                        {inviteLinks[email] && (
                          <div style={{ display: 'flex', gap: '0.375rem', alignItems: 'center' }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={inviteLinks[email]}
                              readOnly
                              style={{ fontSize: '0.75rem', background: 'var(--bg-white)' }}
                            />
                            <button
                              type="button"
                              className={`btn btn-sm ${copiedEmail === email ? 'btn-success' : 'btn-outline-secondary'}`}
                              onClick={() => handleCopyInvite(email)}
                              style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                            >
                              {copiedEmail === email ? '✓ Copied' : 'Copy'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add new caregiver */}
                <label className="form-label" style={{ fontSize: '0.875rem' }}>Add a caregiver</label>
                <div className="input-group">
                  <input
                    type="email"
                    className="form-control"
                    placeholder="caregiver@example.com"
                    value={caregiverEmail}
                    onChange={(e) => setCaregiverEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddCaregiver()}
                  />
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddCaregiver}
                    disabled={!caregiverEmail.trim()}
                  >
                    Add
                  </button>
                </div>
                <p style={{ fontSize: '0.775rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                  An invite link will be generated automatically after adding.
                </p>

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCaregiverModal(false)}>
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChildSelector;
